import React from "react";
import { Download } from "lucide-react";
import {
  EmptyState,
  EmptyStateProps,
  LoadPhase,
  SkeletonCard,
  TABLE_BODY_SKELETON,
} from "../../../components/ui";
import { formatRupees } from "../userFormat";
import type { Invoice, InvoiceStatus } from "../types/users.types";

/**
 * Whether the period an invoice paid for is the one running now.
 *
 * The same two tints the roster's statuses wear, and for the same reason: one
 * palette across the panel means a teal pill always says "this is the live
 * one" and a red one always says "this has run out", whatever table they turn
 * up in.
 */
const INVOICE_STATUS_STYLES: Record<InvoiceStatus, string> = {
  Active: "bg-brand-soft text-[#00899B]",
  Expired: "bg-[#FDECEC] text-[#D14343]",
};

const COLUMNS = [
  { heading: "Invoice", width: "w-[30%]" },
  { heading: "Amount", width: "w-[18%]" },
  { heading: "Date", width: "w-[22%]" },
  { heading: "Status", width: "w-[20%]" },
  // The download column's header is deliberately blank: "Download" over a
  // column of identical icons says nothing the icons have not, and the
  // buttons carry their own labels for anyone not reading by eye.
  { heading: "", width: "w-[10%]" },
];

interface BillingHistoryTableProps {
  invoices: Invoice[];
  /** Stands in for the body when there is no billing history. */
  empty: EmptyStateProps;
  /** Which invoice is being fetched, so its row alone shows the wait. */
  downloadingId?: string | null;
  onDownload: (invoice: Invoice) => void;
  /**
   * Where the rows are. While they are on their way the body says nothing — no
   * rows and no message — and only a load slow enough to notice shimmers, as
   * one block the size of the table. Rows already in hand are left alone
   * either way, so returning to a tab shows the page it was left on rather
   * than shimmering over it.
   */
  phase?: LoadPhase;
}

/** What an account has been invoiced, newest first. */
const BillingHistoryTable: React.FC<BillingHistoryTableProps> = ({
  invoices,
  empty,
  downloadingId = null,
  onDownload,
  phase = "ready",
}) => {
  if (phase !== "ready" && invoices.length === 0) {
    // The table's own area, standing in for the whole list — see
    // `TABLE_BODY_SKELETON`. Hidden from screen readers with the rest of the
    // placeholders; the card above says it is loading in words.
    return (
      <div className="p-4 sm:p-6">
        <SkeletonCard phase={phase} className={TABLE_BODY_SKELETON} />
      </div>
    );
  }

  return (
    <div className="overflow-x-auto custom-scrollbar-overlay">
      <table className="w-full table-fixed border-collapse text-left min-w-[680px]">
        <colgroup>
          {COLUMNS.map((column, index) => (
            <col key={column.heading || `spacer-${index}`} className={column.width} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {COLUMNS.map((column, index) => (
              <th
                key={column.heading || `spacer-${index}`}
                scope="col"
                className="bg-white px-4 sm:px-6 py-3 text-[13px] font-normal text-ink-muted shadow-[inset_0_-1px_0_#E9EDF1]"
              >
                {column.heading || <span className="sr-only">Download</span>}
              </th>
            ))}
          </tr>
        </thead>

        {invoices.length === 0 ? (
          <tbody>
            <tr>
              <td colSpan={COLUMNS.length}>
                <EmptyState {...empty} />
              </td>
            </tr>
          </tbody>
        ) : (
          <tbody>
            {invoices.map((invoice) => (
              <tr key={invoice.id} className="border-b border-line last:border-0">
                <td className="px-4 sm:px-6 py-4 text-[14px] text-ink font-medium whitespace-nowrap">
                  {invoice.id}
                </td>
                <td className="px-4 sm:px-6 py-4 text-[14px] text-ink whitespace-nowrap">
                  {formatRupees(invoice.amount)}
                </td>
                <td className="px-4 sm:px-6 py-4 text-[14px] text-ink whitespace-nowrap">
                  {invoice.date}
                </td>
                <td className="px-4 sm:px-6 py-4">
                  <span
                    className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-[12px] font-medium ${
                      INVOICE_STATUS_STYLES[invoice.status]
                    }`}
                  >
                    {invoice.status}
                  </span>
                </td>
                <td className="px-4 sm:px-6 py-4 text-right">
                  <button
                    type="button"
                    onClick={() => onDownload(invoice)}
                    disabled={downloadingId !== null}
                    // The invoice number is in the label rather than a bare
                    // "Download": a screen reader running down this column would
                    // otherwise hear the same word ten times with nothing to
                    // tell one row from the next.
                    aria-label={`Download invoice ${invoice.id}`}
                    className="p-2 rounded-md text-brand-dark hover:bg-brand-soft disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    {downloadingId === invoice.id ? (
                      <span
                        aria-hidden="true"
                        className="block w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin"
                      />
                    ) : (
                      <Download aria-hidden="true" className="w-5 h-5" />
                    )}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          )}
      </table>
    </div>
  );
};

export default BillingHistoryTable;
